/**
 * @prettier
 */

import { useCallback, useState, useEffect } from 'react';
import _values from 'lodash/values';
import { MOBILE_MAX } from 'lib/responsive';
import { CARD_TYPES } from 'cards/constants/CardTypes';
import { isSingleSampleLimitExhausted, getCardSuiteDetails, getTemplate, uploadPhoto } from './api';
import ApiService from '../../../../../util/api';
import { normalizeDateString, dateIsComplete, getValidatedDate } from './helpers';

export function useSingleSampleLimitExhausted(cardType) {
  const globalKey = 'ZOLA_INVITATIONS_SINGLE_SAMPLE_MODAL_SAMPLE_LIMIT_EXHAUSTED_PROMISE';

  const [isExhausted, setIsExhausted] = useState(false);

  const updateIsExhausted = useCallback(
    exhausted => isExhausted !== exhausted && setIsExhausted(exhausted),
    [setIsExhausted]
  );

  const fetchIsExhausted = useCallback(() => {
    window[globalKey] = isSingleSampleLimitExhausted(cardType);
    return window[globalKey].then(updateIsExhausted);
  }, [cardType, updateIsExhausted]);

  /**
   * Cache whether the sample limit is exhausted in a global variable
   */
  useEffect(() => {
    if (cardType) {
      if (window[globalKey] && window[globalKey].then) {
        window[globalKey].then(updateIsExhausted);
      } else {
        fetchIsExhausted();
      }
    }
  });

  return [isExhausted, fetchIsExhausted];
}

export function useDebounce(callback, delay, dependencies) {
  useEffect(() => {
    const handler = setTimeout(callback, delay);

    return () => {
      clearTimeout(handler);
    };
  }, dependencies);
}

export function usePreviewTemplate(cardSuiteDetails, form, sampleNotCustomizable, isMagnet) {
  const [template, setTemplate] = useState(null);

  const weddingDetails = form && form.weddingDetails;
  const variation = form && form.customizations.variation;
  const color = form && form.customizations.color;

  useEffect(() => {
    getTemplate(
      cardSuiteDetails,
      variation,
      color,
      weddingDetails,
      sampleNotCustomizable,
      isMagnet
    ).then(setTemplate);
  }, [
    variation,
    color,
    sampleNotCustomizable,
    isMagnet,
    cardSuiteDetails,
    ...(weddingDetails ? _values(weddingDetails) : []),
  ]);

  return template;
}

export function useCardSuiteDetails(cardSuiteUUID, isAdminView) {
  const [cardSuiteDetails, setCardSuiteDetails] = useState(null);

  useEffect(() => {
    if (cardSuiteUUID) {
      getCardSuiteDetails(cardSuiteUUID, isAdminView).then(setCardSuiteDetails);
    }
  }, [cardSuiteUUID, isAdminView]);

  return cardSuiteDetails;
}

export function usePhotoObjectURL(photo) {
  const [photoURL, setPhotoURL] = useState();

  useEffect(() => {
    if (photo && photo.file) {
      setPhotoURL(URL.createObjectURL(photo.displayedBlob));
    }

    return () => photoURL && URL.revokeObjectURL(photoURL);
  }, [photo]);

  return photoURL;
}

const MAX_PHOTO_CACHE_ENTRIES = 10e3;

export function usePhotoCache() {
  const sessionStorageKey = 'ZOLA_INVITATIONS_SINGLE_SAMPLE_MODAL_PHOTO_CACHE_STORE';

  const [cache, setCache] = useState(null);

  useEffect(() => {
    const recoveredCacheJSON = sessionStorage.getItem(sessionStorageKey) || '{}';

    let recoveredCache = {};

    try {
      recoveredCache = JSON.parse(recoveredCacheJSON);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Cannot load photo cache from sessionStorage at: ${sessionStorageKey}`);
    }

    if (Object.keys(recoveredCache).length > MAX_PHOTO_CACHE_ENTRIES) {
      setCache({});
    }

    setCache(recoveredCache);
  }, []);

  const updateCache = newCache => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(newCache));
    setCache(newCache);
  };

  const photoUploadHandler = (photo, crop) => {
    const key = `${photo.id}-${crop.x}-${crop.y}-${crop.width}-${crop.height}`;

    if (cache && cache[key]) {
      return Promise.resolve(cache[key]);
    }

    return uploadPhoto(photo, crop).then(photoUUID => {
      updateCache({ ...cache, [key]: photoUUID });

      return photoUUID;
    });
  };

  return photoUploadHandler;
}

export function useSpecialTypeSampleView(sampleSku) {
  const [skuData, setSkuData] = useState();

  useEffect(() => {
    if (!sampleSku) {
      setSkuData();
      return;
    }

    ApiService.post('/web-api/v1/products/find', { sku_ids: [sampleSku] }).then(data => {
      const newSkuData = skuData || {};

      if (
        data &&
        data[0] &&
        data[0].status &&
        data[0].product_look_views &&
        data[0].product_look_views[0] &&
        data[0].product_look_views[0].sku_previews &&
        data[0].product_look_views[0].sku_previews[0]
      ) {
        const preview = data[0].product_look_views[0].sku_previews[0];

        newSkuData.preview = preview;
      }

      if (
        data &&
        data[0] &&
        data[0].status &&
        data[0].product_look_views &&
        data[0].product_look_views[0] &&
        data[0].product_look_views[0].images &&
        data[0].product_look_views[0].images[0] &&
        data[0].product_look_views[0].images[0].aspect_ratios
      ) {
        const images = data[0].product_look_views[0].images[0].aspect_ratios;
        const imageKey = images['3x4'] ? '3x4' : '1x1';
        const image = images[imageKey] && images[imageKey].medium;

        newSkuData.image = image;
      }

      setSkuData(newSkuData);
    });
  }, [sampleSku, skuData]);

  return skuData;
}

export function useModalCloseCallback() {
  return useCallback(({ modalOptions, setModalOptions, setModalContext }) => {
    if (modalOptions.preventClose) {
      setModalContext({ showExitOverlay: true });
      setModalOptions({ hideClose: true });
    }
  }, []);
}

export function useDateSetter(fieldSetter) {
  return useCallback(
    inputDate => {
      const normalizedDate = normalizeDateString(inputDate);
      fieldSetter(dateIsComplete(inputDate) ? getValidatedDate(normalizedDate) : normalizedDate);
    },
    [fieldSetter]
  );
}

export function useMobileCheck() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);

    const listener = media => setIsMobile(media.matches);

    mediaQuery.addListener(listener);

    return () => mediaQuery.removeListener(listener);
  }, [setIsMobile]);

  return isMobile;
}

export const getMostPopularCardFromFamily = familyName => {
  const [cardInfo, setCardInfo] = useState({});

  useEffect(() => {
    ApiService.post('/web-api/v1/card-catalog/search/faceted', {
      offset: 0,
      limit: 12,
      active_only: true,
      has_foil: null,
      has_custom_photo: null,
      has_magnet: null,
      orientation: null,
      third_party: null,
      family: familyName,
      lead_card_type: null,
    })
      .then(json => {
        // Only accept day-of cards then get the highest ranked
        const mostPopularCard =
          json.suites.filter(suite => [CARD_TYPES.invitation].includes(suite.lead_card.type))[0] ||
          {};
        const newCardInfo = {};
        newCardInfo.uuid = mostPopularCard.uuid;
        newCardInfo.cardType = mostPopularCard.lead_card && mostPopularCard.lead_card.type;
        newCardInfo.singleSampleSku = mostPopularCard.single_sample_sku;
        newCardInfo.singleSampleAvailable = mostPopularCard.single_sample_available;
        newCardInfo.isMagnet = mostPopularCard.magnet;
        newCardInfo.isLetterpress =
          mostPopularCard.lead_card && mostPopularCard.lead_card.letterpress;
        newCardInfo.sampleCard = mostPopularCard;
        setCardInfo(newCardInfo);
      })
      .catch(() => {});
  }, [familyName]);

  return cardInfo;
};



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/util/hooks.js